import { atom } from 'jotai'
import { atomWithQuery } from 'jotai-tanstack-query'
import { hc, type InferResponseType } from 'hono/client'
import type { MeApp } from '@backend/auth'
import { apiFetch, apiBase, AuthRequiredError } from 'txom-frontend-libs'

const meApi = hc<MeApp>(apiBase + '/api/me', { fetch: apiFetch })

type MeResponse = InferResponseType<typeof meApi.index.$get, 200>
type User = NonNullable<MeResponse['user']>

// ============================================================================
// QUERY ATOM (data fetching)
// ============================================================================

export const currentUserQueryAtom = atomWithQuery(() => ({
  queryKey: ['currentUser'],
  queryFn: async () => {
    try {
      const res = await meApi.index.$get()
      const data = await res.json()
      return data.user
    } catch {
      // Auth service unavailable — treat as not logged in
      return null
    }
  },
  retry: false,
}))

// ============================================================================
// DERIVED ATOMS
// ============================================================================

// Empty user object used during loading state (check user.id to determine if loaded)
const emptyUser: User = {
  id: '',
  email: '',
  name: null,
  role: 'user' as const,
  createdBy: null,
  updatedBy: null,
  createdAt: null,
  updatedAt: null,
}

/**
 * Use this atom in pages that require authentication.
 * Returns emptyUser (with id='') while loading.
 * Throws AuthRequiredError when query completes and user is null.
 * Check user.id to determine if still loading.
 */
export const authRequiredAtom = atom((get) => {
  const { data, isPending } = get(currentUserQueryAtom)
  if (isPending) return emptyUser
  if (!data) throw new AuthRequiredError()
  return data
})

